import React from "react";
import MediaFileVideoFacade from "./components/mediafile.video.facade";
import ExternalVideoFacade from "./components/external.video.facade";
import VimeoVideoFacade from "./components/vimeo.video.facade";
import YoutubeFacade from "./components/youtube.video.facade";
import Suspense from "src/components/suspense/suspense";

type Props = {
  className?: string;
  videoClassname?: string;
  backgroundColor?: string;

  source: TVideoType;

  title?: string;
  url?: string;

  placeholder?: string;

  autoPlay: boolean;
  loop: boolean;
  removeControls: boolean;
  asBackground: boolean;

  freeze: boolean;
}

export default class VideoComponent extends React.Component<Props>{

  state: TVideComponentState = {
    mounted: false,
  }

  get fileUrl() {
    const src = this.props.url || "";
    let prefix = "";
    if (src.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.(?:.{3}|.{4})/)) prefix = "https://public-assets.spotliodata.com/";
    let fullsrc = prefix + src;
    return fullsrc;
  }

  get colors(){
    if (this.props.backgroundColor) return { '--video-bg-color': this.props.backgroundColor } as React.CSSProperties;
    return {}
  }

  getLazyComponent() {
    switch (this.props.source) {
      case "file": return React.lazy(() => import("./components/mediafile.video.component"));
      case "url": return React.lazy(() => import("./components/external.video.component"));
      case "vimeo": return React.lazy(() => import("./components/vimeo.video.component"));
      case "youtube": return React.lazy(() => import("./components/youtube.video.component"));
      default: return () => <div className="w-full aspect-[16/9] bg-slate-200 rounded" />
    }
  }

  getSrc() {
    switch (this.props.source) {
      case "file": return this.fileUrl || "";
      case "url": return this.props.url || "";
      case "vimeo": return this.props.url || "";
      case "youtube": return this.props.url || "";
      default: return this.props.url || "";
    }
  }

  getVideoProps(): TVideoComponentProps {
    return {
      title: this.props.title || this.props.source,
      src: this.getSrc(),
      placeholder: this.props.placeholder,
      removeControls: this.props.removeControls,
      loop: this.props.loop,
      autoPlay: this.props.autoPlay,
      asBackground: this.props.asBackground,
      className: `-z-[1] ${this.props.videoClassname}` || "-z-[1]"
    };
  }

  renderComponent() {
    const Component = this.getLazyComponent();
    const props = this.getVideoProps();
    return <Component {...props} />
  }

  renderFacade() {
    const props = this.getVideoProps();
    const { source } = this.props;
    if (source === "file") return <MediaFileVideoFacade {...props} />
    if (source === "url") return <ExternalVideoFacade {...props} />
    if (source === "vimeo") return <VimeoVideoFacade {...props} />
    if (source === "youtube") return <YoutubeFacade {...props} />
    return <div className="w-full aspect-[16/9] bg-slate-200 rounded" />
  }

  componentDidMount(): void {
    if (this.props.freeze) return;
    React.startTransition(() => { this.setState({mounted: true}) });
  }

  render(): React.ReactNode {
    const { className = "" } = this.props;
    return (
      <div className={`w-full h-full ${className} inset-0 bg-[color:var(--video-bg-color)]`} style={this.colors}>
        <div className={`absolute inset-0 bg-[color:var(--video-overlay-color)]`}></div>
        <div className="w-full h-full bg-[color:var(--video-overlay-color)]">
          <Suspense fallback={this.renderFacade()}>
            {this.state.mounted ? this.renderComponent() : this.renderFacade()}
          </Suspense>
        </div>
      </div>
    )
  }
}